// Multiplexed household realtime hook
// - Opens a single Supabase channel per household instead of five separate ones
// - Routes postgres_changes payloads to the appropriate React Query cache by table name
// - Handles presence tracking (join/leave/sync) on the same channel
// - Replaces usePresence, useInventory subscription, useShoppingList subscription,
//   and useHouseholdSubscription — mount this once via PresenceContext

import type { User } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { realtimeWorker } from "../lib/realtimeWorker";
import { supabase } from "../lib/supabase";
import type { Household } from "../services/householdService";
import type { InventoryItem } from "../services/inventoryService";
import type { ShoppingListItem } from "../services/shoppingListService";
import { useQueryClient } from "./useDataCache";
import type { PresenceMeta } from "./usePresenceContext";
import { USER_HOUSEHOLD_ID_KEY, useUser } from "./useUser";

type PresenceCleanup = {
  handleVisibilityChange: () => void;
  handlePageShow: (event: PageTransitionEvent) => void;
  handlePageHide: () => void;
};

type ChannelWithCleanup = {
  channelId: string | null;
  __presence_cleanup?: PresenceCleanup;
};

// Single hook that owns the one realtime channel for the current household.
// Returns the same presence-shaped API as usePresence so PresenceContext needs
// only a one-line import swap.
export function useHouseholdRealtime() {
  const { householdId } = useUser();
  const queryClient = useQueryClient();
  const [presenceMap, setPresenceMap] = useState<Map<string, PresenceMeta[]>>(
    new Map(),
  );
  const [isOnline, setIsOnline] = useState(false);
  const channelRef = useRef<ChannelWithCleanup | null>(null);
  const mountedRef = useRef(true);
  const userRef = useRef<User | null>(null);
  const wasDisconnectedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    if (!householdId) return;

    const setup = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // On mobile PWA resume, Supabase may be mid-token-refresh and getSession()
      // can transiently return null. Fall back to getUser() so we always have
      // a valid user identity before subscribing the realtime channel.
      const user =
        session?.user ?? (await supabase.auth.getUser()).data.user ?? null;
      if (!user || !mountedRef.current) return;
      userRef.current = user;

      const channelId = `household:${householdId}`;
      channelRef.current = { channelId, __presence_cleanup: undefined };

      // ─── Presence helpers ────────────────────────────────────────────────

      const syncStateFrom = (state: unknown) => {
        try {
          const s = state as Record<string, PresenceMeta[]>;
          const map = new Map<string, PresenceMeta[]>();
          Object.entries(s || {}).forEach(([key, val]) => {
            map.set(key, val as PresenceMeta[]);
          });
          if (mountedRef.current) setPresenceMap(map);
        } catch (err) {
          console.warn(
            "🟡 useHouseholdRealtime: error reading presence state",
            err,
          );
        }
      };

      const trackPresence = () => {
        if (!mountedRef.current || !userRef.current) return;
        try {
          realtimeWorker.trackPresence(channelId, {
            user_id: userRef.current.id,
            email: userRef.current.email || null,
            online_at: new Date().toISOString(),
          });
        } catch (err) {
          console.warn("🟡 useHouseholdRealtime: track failed", err);
        }
      };

      // Type guards for presence message payloads forwarded from the worker
      const isObject = (v: unknown): v is Record<string, unknown> =>
        typeof v === "object" && v !== null;

      const hasState = (
        v: unknown,
      ): v is { state: Record<string, PresenceMeta[]> } =>
        isObject(v) &&
        "state" in v &&
        typeof (v as Record<string, unknown>).state === "object";

      const hasNewPresences = (
        v: unknown,
      ): v is { newPresences: Record<string, PresenceMeta[]> } =>
        isObject(v) &&
        "newPresences" in v &&
        typeof (v as Record<string, unknown>).newPresences === "object";

      const hasLeftPresences = (
        v: unknown,
      ): v is { leftPresences: Record<string, PresenceMeta[]> } =>
        isObject(v) &&
        "leftPresences" in v &&
        typeof (v as Record<string, unknown>).leftPresences === "object";

      // Handle presence events coming from the worker and update local state
      const presenceHandler = (event: string, message: unknown) => {
        if (!mountedRef.current) return;

        if (event === "sync" && hasState(message)) {
          syncStateFrom(message.state);
          return;
        }

        if (event === "join" || event === "leave") {
          // Prefer explicit state if present, otherwise fall back to new/left presences
          if (hasState(message)) {
            syncStateFrom(message.state);
          } else if (hasNewPresences(message)) {
            syncStateFrom(message.newPresences);
          } else if (hasLeftPresences(message)) {
            syncStateFrom(message.leftPresences);
          }
        }
      };

      // ─── Postgres-changes data router ────────────────────────────────────

      const handleData = (payload: unknown) => {
        if (!mountedRef.current) return;

        const p = payload as {
          table?: string;
          eventType: string;
          new?: unknown;
          old?: unknown;
        };

        if (p.table === "inventory_items") {
          // ── inventory_items ───────────────────────────────────────────────
          if (p.eventType === "INSERT") {
            const newItem = p.new as InventoryItem;
            queryClient.setQueryData(
              ["inventory", newItem.location, householdId],
              (old: InventoryItem[] | undefined) => {
                if (!old) return [newItem];
                const exists = old.some((item) => item.id === newItem.id);
                return exists ? old : [newItem, ...old];
              },
            );
            queryClient.setQueryData(
              ["inventory", "all", householdId],
              (old: InventoryItem[] | undefined) => {
                if (!old) return [newItem];
                const exists = old.some((item) => item.id === newItem.id);
                return exists ? old : [newItem, ...old];
              },
            );
          } else if (p.eventType === "UPDATE") {
            const updatedItem = p.new as InventoryItem;
            const oldItem = p.old as InventoryItem;

            const updateCache = (old: InventoryItem[] | undefined) =>
              old
                ? old.map((item) =>
                    item.id === updatedItem.id ? updatedItem : item,
                  )
                : old;

            queryClient.setQueryData(
              ["inventory", updatedItem.location, householdId],
              updateCache,
            );
            queryClient.setQueryData(
              ["inventory", "all", householdId],
              updateCache,
            );

            // When an item moves locations, evict it from the old location cache
            if (oldItem.location !== updatedItem.location) {
              queryClient.setQueryData(
                ["inventory", oldItem.location, householdId],
                (old: InventoryItem[] | undefined) =>
                  old ? old.filter((item) => item.id !== updatedItem.id) : old,
              );
            }
          } else if (p.eventType === "DELETE") {
            const deletedItem = p.old as InventoryItem;
            const removeDeleted = (old: InventoryItem[] | undefined) =>
              old ? old.filter((item) => item.id !== deletedItem.id) : old;

            queryClient.setQueryData(
              ["inventory", "all", householdId],
              removeDeleted,
            );
            queryClient.setQueryData(
              ["inventory", "fridge", householdId],
              removeDeleted,
            );
            queryClient.setQueryData(
              ["inventory", "freezer", householdId],
              removeDeleted,
            );
            queryClient.setQueryData(
              ["inventory", "pantry", householdId],
              removeDeleted,
            );
          }
        } else if (p.table === "shopping_list_items") {
          // ── shopping_list_items ───────────────────────────────────────────
          if (p.eventType === "INSERT") {
            const newItem = p.new as ShoppingListItem;
            queryClient.setQueryData(
              ["shoppingList", householdId],
              (old: ShoppingListItem[] | undefined) => {
                if (!old) return [newItem];
                const exists = old.some((item) => item.id === newItem.id);
                return exists ? old : [newItem, ...old];
              },
            );
          } else if (p.eventType === "UPDATE") {
            const updatedItem = p.new as ShoppingListItem;
            queryClient.setQueryData(
              ["shoppingList", householdId],
              (old: ShoppingListItem[] | undefined) =>
                old
                  ? old.map((item) =>
                      item.id === updatedItem.id ? updatedItem : item,
                    )
                  : old,
            );
          } else if (p.eventType === "DELETE") {
            const deletedItem = p.old as ShoppingListItem;
            queryClient.setQueryData(
              ["shoppingList", householdId],
              (old: ShoppingListItem[] | undefined) =>
                old ? old.filter((item) => item.id !== deletedItem.id) : old,
            );
          }
        } else if (p.table === "households") {
          // ── households ───────────────────────────────────────────────────
          // Direct name/settings change — update the cached household record
          const updatedHousehold = p.new as Household | undefined;
          if (updatedHousehold) {
            queryClient.setQueryData(
              ["household", householdId],
              updatedHousehold,
            );
          }
        } else if (p.table === "household_members") {
          // ── household_members ─────────────────────────────────────────────
          queryClient.invalidateQueries({ queryKey: ["householdMembers"] });
          queryClient.invalidateQueries({
            queryKey: ["household", householdId],
          });
          // Re-check household membership in case the current user was removed
          queryClient.invalidateQueries({ queryKey: USER_HOUSEHOLD_ID_KEY });
        } else if (p.table === "household_invitations") {
          // ── household_invitations ─────────────────────────────────────────
          queryClient.invalidateQueries({ queryKey: ["householdInvitations"] });
        }
      };

      // ─── Channel status ───────────────────────────────────────────────────

      const statusHandler = (status: string) => {
        if (!mountedRef.current) return;

        if (status === "SUBSCRIBED") {
          console.log("🟢 Household realtime status:", status);
          // Cancel any pending reconnect timer — we're live
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          reconnectAttemptRef.current = 0;
          trackPresence();
          setIsOnline(true);

          // Reconnected after a disconnect — invalidate data queries (but NOT
          // the household record itself, which would race with the loading guard
          // and flash "No Household Found").
          if (wasDisconnectedRef.current) {
            wasDisconnectedRef.current = false;
            queryClient.invalidateQueries({ queryKey: ["inventory"] });
            queryClient.invalidateQueries({ queryKey: ["shoppingList"] });
            queryClient.invalidateQueries({ queryKey: ["householdMembers"] });
            queryClient.invalidateQueries({
              queryKey: ["householdInvitations"],
            });
            queryClient.invalidateQueries({ queryKey: USER_HOUSEHOLD_ID_KEY });
          }
        } else if (
          status === "CHANNEL_ERROR" ||
          status === "TIMED_OUT" ||
          status === "CLOSED"
        ) {
          const logFn =
            status === "CHANNEL_ERROR" ? console.error : console.warn;
          logFn("🟡 Household realtime status:", status);
          wasDisconnectedRef.current = true;
          setIsOnline(false);
          scheduleReconnect();
        }
      };

      // ─── Subscribe ────────────────────────────────────────────────────────

      const accessToken = session?.access_token ?? undefined;

      realtimeWorker.subscribeHousehold(
        channelId,
        [
          {
            table: "households",
            filter: `id=eq.${householdId}`,
          },
          {
            table: "inventory_items",
            filter: `household_id=eq.${householdId}`,
          },
          {
            table: "shopping_list_items",
            filter: `household_id=eq.${householdId}`,
          },
          {
            table: "household_members",
            filter: `household_id=eq.${householdId}`,
          },
          {
            table: "household_invitations",
            filter: `household_id=eq.${householdId}`,
          },
        ],
        handleData,
        presenceHandler,
        statusHandler,
        accessToken,
        user.id,
      );

      // ─── Reconnection helpers ─────────────────────────────────────────────

      // Tear down the current channel and re-run setup. Adds a short delay so
      // the worker's unsubscribe message is fully processed before we re-subscribe.
      const triggerReconnect = () => {
        if (!mountedRef.current) return;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        const ch = channelRef.current;
        if (ch?.__presence_cleanup) {
          document.removeEventListener(
            "visibilitychange",
            ch.__presence_cleanup.handleVisibilityChange,
          );
          window.removeEventListener(
            "pageshow",
            ch.__presence_cleanup.handlePageShow,
          );
          window.removeEventListener(
            "pagehide",
            ch.__presence_cleanup.handlePageHide,
          );
        }
        if (ch?.channelId) {
          realtimeWorker.unsubscribe(ch.channelId);
        }
        channelRef.current = null;
        // Short delay to let the worker process the unsubscribe before we
        // post a new subscribeHousehold message.
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (!mountedRef.current) return;
          setup().catch((err) => {
            console.error("🔴 useHouseholdRealtime re-setup error:", err);
          });
        }, 300);
      };

      // Exponential-backoff reconnect scheduler — called from statusHandler
      // when the channel reports CHANNEL_ERROR / TIMED_OUT / CLOSED.
      const scheduleReconnect = () => {
        if (!mountedRef.current) return;
        if (reconnectTimerRef.current) return; // already scheduled
        const attempt = reconnectAttemptRef.current;
        reconnectAttemptRef.current = attempt + 1;
        // Cap at ~60 s: 2s, 4s, 8s, 16s, 32s, 60s, 60s …
        const delay = Math.min(2000 * Math.pow(2, attempt), 60_000);
        console.log(
          `🟡 Realtime: scheduling reconnect attempt ${attempt + 1} in ${delay}ms`,
        );
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (!mountedRef.current) return;
          triggerReconnect();
        }, delay);
      };

      // ─── Visibility / page-lifecycle presence re-tracking ─────────────────

      const handleVisibilityChange = () => {
        if (!document.hidden && mountedRef.current) {
          if (wasDisconnectedRef.current) {
            // The channel dropped while backgrounded — trigger an immediate reconnect
            triggerReconnect();
          } else {
            trackPresence();
          }
        }
      };

      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          trackPresence();
        }
      };

      const handlePageHide = () => {
        // Best-effort: tell worker to untrack current presence entry
        if (userRef.current) {
          realtimeWorker.trackPresence(channelId, {
            user_id: userRef.current.id,
            online_at: new Date().toISOString(),
          });
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("pageshow", handlePageShow);
      window.addEventListener("pagehide", handlePageHide);

      if (channelRef.current) {
        channelRef.current.__presence_cleanup = {
          handleVisibilityChange,
          handlePageShow,
          handlePageHide,
        };
      }
    };

    setup().catch((err) => {
      console.error("🔴 useHouseholdRealtime setup error:", err);
    });

    return () => {
      mountedRef.current = false;
      setIsOnline(false);
      setPresenceMap(new Map());

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const channel = channelRef.current;
      if (channel) {
        try {
          if (channel.__presence_cleanup?.handleVisibilityChange) {
            document.removeEventListener(
              "visibilitychange",
              channel.__presence_cleanup.handleVisibilityChange,
            );
          }
          if (channel.__presence_cleanup?.handlePageShow) {
            window.removeEventListener(
              "pageshow",
              channel.__presence_cleanup.handlePageShow,
            );
          }
          if (channel.__presence_cleanup?.handlePageHide) {
            window.removeEventListener(
              "pagehide",
              channel.__presence_cleanup.handlePageHide,
            );
          }
        } catch {
          // Ignore cleanup errors
        }

        // Unsubscribe the single multiplexed household channel
        if (channel.channelId) {
          realtimeWorker.unsubscribe(channel.channelId);
        }
        // Remove all channels from the supabase-js client so the underlying
        // WebSocket connection is fully released and the Postgres Realtime
        // slot is freed. Without this the channel lingers in the client's
        // internal registry even after the worker unsubscribes.
        supabase.removeAllChannels().catch(() => {
          // Best-effort — ignore errors during cleanup
        });
      }

      channelRef.current = null;
      userRef.current = null;
    };
  }, [householdId, queryClient]);

  return {
    presenceMap,
    isOnline,
    getUserPresence: (userId: string) => presenceMap.get(userId) || [],
  };
}
